import { useEffect, useState } from "react";
import { api, apiUrl, fmtINR, fmtCompact, fmtPeriod } from "@/lib/api";
import { PageHeader, Btn, Modal, Input, Select, ComboSelect, Badge, MonthPicker } from "@/components/Shared";
import { Plus, Pencil, Trash2, Download, CheckCircle2, Info, X } from "lucide-react";
import { toast } from "sonner";
import { openConfirm } from "@/lib/dialogs";

import useMonthlyAccounting from "./monthly_accounting/useMonthlyAccounting";
import PendingApprovalsTab from "./monthly_accounting/PendingApprovalsTab";
import MonthlySummaryTab from "./monthly_accounting/MonthlySummaryTab";
import ExpensesTab from "./monthly_accounting/ExpensesTab";
import SettlementPanel from "./monthly_accounting/SettlementPanel";

const EMPTY_EXP = { month: "", expense_label: "", amount: 0, reimbursement_to: "BizAssist", comments: "" };
const EMPTY_INC = { month: "", commission_received: 0, notes: "" };
const EMPTY_BULK_ROW = () => ({ key: Math.random().toString(36).slice(2, 9), month: "", expense_label: "", amount: "", reimbursement_to: "BizAssist", comments: "" });

export default function MonthlyAccounting() {
  const [data, setData] = useState({ expenses: [], incomes: [], summary: [], available_fys: [] });
  const [fyFilter, setFyFilter] = useState("");
  const [monthFilter, setMonthFilter] = useState("");
  const [openExp, setOpenExp] = useState(false);
  const [formExp, setFormExp] = useState(EMPTY_EXP);
  const [editExpId, setEditExpId] = useState(null);
  const [openInc, setOpenInc] = useState(false);
  const [formInc, setFormInc] = useState(EMPTY_INC);
  const [openBulk, setOpenBulk] = useState(false);
  const [bulkRows, setBulkRows] = useState([EMPTY_BULK_ROW(), EMPTY_BULK_ROW(), EMPTY_BULK_ROW()]);
  const [bulkDefaultMonth, setBulkDefaultMonth] = useState("");
  const [openPay, setOpenPay] = useState(false);
  const [payForm, setPayForm] = useState({ id: null, month: "", person: "NK", amount_paid: 0, paid_via: "Bank Transfer", paid_date: "", notes: "" });
  const [allocDetail, setAllocDetail] = useState(null);  // {month, person, lines: [...]}
  const [settlement, setSettlement] = useState(null);    // {month, person, summary} when panel open
  const [openTmpl, setOpenTmpl] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tmplForm, setTmplForm] = useState({ id: null, label: "", amount: "", reimbursement_to: "BizAssist", start_month: "", end_month: "", notes: "" });
  // Reject-reason modal — replaces the old window.prompt() per UX feedback.
  const [rejectModal, setRejectModal] = useState(null);  // null | { lid, reason }
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalBusy, setApprovalBusy] = useState(null);  // ledger id currently being processed
  // Edit-before-approve modal: lets the checker tweak amount_paid + notes
  // (and the existing CTC stays locked on the server). Then save & approve
  // in one click, or just save (still pending) for further review.
  const [approvalEdit, setApprovalEdit] = useState(null);  // null | {row, amount_paid, notes}

  // ----- Derived data (filtered list, KPIs, available months, monthExpenses) -
  const { filtered, kpis, availableMonths, monthExpenses } = useMonthlyAccounting(data, monthFilter);
  const {
    kpiInc, kpiExp, kpiNet,
    kpiNkOutstanding, kpiRrOutstanding,
    kpiNkOpenMonths, kpiRrOpenMonths,
  } = kpis;

  const load = async () => {
    const params = new URLSearchParams();
    if (fyFilter) params.set("fy", fyFilter);
    const [r, tpl, pa] = await Promise.all([
      api.get(`/monthly-accounting?${params.toString()}`),
      api.get("/monthly-accounting/templates"),
      api.get("/people/salary-ledger/pending"),
    ]);
    setData(r.data);
    setTemplates(tpl.data || []);
    setPendingApprovals(pa.data?.rows || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fyFilter]);

  // ----- Payment handlers ----------------------------------------------------
  const openPaymentNew = (month, person, suggestedAmount) => {
    setPayForm({
      id: null, month, person,
      amount_paid: suggestedAmount > 0 ? Math.round(suggestedAmount) : 0,
      paid_via: "Bank Transfer",
      paid_date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setOpenPay(true);
  };
  const openPaymentEdit = (line) => {
    setPayForm({
      id: line.id, month: line.month, person: line.person,
      amount_paid: line.amount_paid || 0,
      paid_via: line.paid_via || "Bank Transfer",
      paid_date: line.paid_date || "",
      notes: line.notes || "",
    });
    setOpenPay(true);
  };
  const savePayment = async () => {
    try {
      // Drop null id on create — Pydantic rejects `id: null`
      const { id, ...rest } = payForm;
      const body = { ...rest, amount_paid: Number(rest.amount_paid) };
      if (id) await api.put(`/monthly-accounting/nk-rr-payment/${id}`, body);
      else await api.post("/monthly-accounting/nk-rr-payment", body);
      toast.success(`${payForm.person} payment saved`);
      setOpenPay(false); await load();
      // If settlement panel open, refresh its source
      if (settlement) {
        const fresh = (await api.get(`/monthly-accounting?${new URLSearchParams(fyFilter ? { fy: fyFilter } : {})}`)).data.summary.find((x) => x.month === settlement.month);
        if (fresh) setSettlement({ ...settlement, summary: fresh });
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ") : (e?.message || "Failed");
      toast.error(msg);
    }
  };
  const deletePayment = async (id) => {
    if (!await openConfirm({ title: "Delete payment line?", message: "This payment will no longer count towards the NK/RR settlement for the month.", confirmLabel: "Delete payment", danger: true })) return;
    try {
      await api.delete(`/monthly-accounting/nk-rr-payment/${id}`);
      toast.success("Deleted"); await load();
      if (settlement) {
        const fresh = (await api.get(`/monthly-accounting?${new URLSearchParams(fyFilter ? { fy: fyFilter } : {})}`)).data.summary.find((x) => x.month === settlement.month);
        if (fresh) setSettlement({ ...settlement, summary: fresh });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openSettlement = (s, person) => setSettlement({ month: s.month, person, summary: s });
  const openAlloc = (s, person) => setAllocDetail({ month: s.month, person, lines: person === "NK" ? (s.nk_salary_detail || []) : (s.rr_salary_detail || []) });

  // ----- Salary approval handlers -------------------------------------------
  const openApprovalEdit = (row) => {
    setApprovalEdit({ row, amount_paid: row.amount_paid || 0, notes: row.notes || "" });
  };
  const saveApprovalEdit = async (alsoApprove) => {
    if (!approvalEdit) return;
    const { row } = approvalEdit;
    setApprovalBusy(row.id);
    try {
      // Persist the edited amount_paid + notes via the standard PUT endpoint.
      // (amount_due stays locked to CTC on the server regardless of payload.)
      const body = {
        person_id: row.person_id,
        month: row.month,
        amount_paid: Number(approvalEdit.amount_paid || 0),
        amount_due: row.amount_due || 0,
        notes: approvalEdit.notes || "",
      };
      await api.put(`/people/${row.person_id}/salary-ledger/${row.id}`, body);
      if (alsoApprove) {
        await api.post(`/people/salary-ledger/${row.id}/approve`, { note: "edited & approved" });
        toast.success("Edited & approved — deduction now active");
      } else {
        toast.success("Saved (still pending)");
      }
      setApprovalEdit(null);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Failed"));
    }
    setApprovalBusy(null);
  };
  const approveSalary = async (lid, note = "") => {
    setApprovalBusy(lid);
    try {
      await api.post(`/people/salary-ledger/${lid}/approve`, { note });
      toast.success("Approved — deduction now active");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Failed"));
    }
    setApprovalBusy(null);
  };
  // Open in-app modal instead of native window.prompt — keeps look & feel
  // consistent and matches the rest of the destructive-action UX.
  const rejectSalary = (lid) => setRejectModal({ lid, reason: "" });
  const confirmReject = async () => {
    const { lid, reason } = rejectModal;
    setRejectModal(null);
    setApprovalBusy(lid);
    try {
      await api.post(`/people/salary-ledger/${lid}/reject`, { note: reason });
      toast.success("Rejected — entry will not deduct from payable");
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (e?.message || "Failed"));
    }
    setApprovalBusy(null);
  };

  // ----- Recurring template handlers ----------------------------------------
  const openNewTemplate = () => {
    setTmplForm({ id: null, label: "", amount: "", reimbursement_to: "BizAssist", start_month: "", end_month: "", notes: "" });
    setOpenTmpl(true);
  };
  const editTemplate = (t) => { setTmplForm({ ...t }); setOpenTmpl(true); };
  const saveTemplate = async () => {
    if (!tmplForm.label || !Number(tmplForm.amount) || !tmplForm.start_month) {
      toast.error("Label, amount and start month are required"); return;
    }
    const { id, ...rest } = tmplForm;
    const payload = { ...rest, amount: Number(rest.amount) };
    try {
      if (id) await api.put(`/monthly-accounting/templates/${id}`, payload);
      else await api.post("/monthly-accounting/templates", payload);
      toast.success("Saved"); setOpenTmpl(false); load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) ? detail.map((d) => d.msg).join(", ") : "Failed";
      toast.error(msg);
    }
  };
  const removeTemplate = async (id) => {
    if (!await openConfirm({ title: "Delete recurring template?", message: "Past auto-generated rows already in the ledger stay. This only stops new automatic entries from this template.", confirmLabel: "Delete template", danger: true })) return;
    await api.delete(`/monthly-accounting/templates/${id}`); toast.success("Deleted"); load();
  };

  // ----- Expense handlers ---------------------------------------------------
  const saveExp = async () => {
    try {
      const p = { ...formExp, amount: Number(formExp.amount) };
      if (editExpId) await api.put(`/monthly-accounting/expense/${editExpId}`, p);
      else await api.post("/monthly-accounting/expense", p);
      toast.success("Saved"); setOpenExp(false); setFormExp(EMPTY_EXP); setEditExpId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const removeExp = async (id) => {
    if (!await openConfirm({ title: "Delete expense?", message: "The amount will no longer be deducted from the month's net revenue.", confirmLabel: "Delete expense", danger: true })) return;
    await api.delete(`/monthly-accounting/expense/${id}`); toast.success("Deleted"); load();
  };
  const editExp = (e) => { setFormExp({ ...e }); setEditExpId(e.id); setOpenExp(true); };

  const openBulkModal = () => {
    setBulkRows([EMPTY_BULK_ROW(), EMPTY_BULK_ROW(), EMPTY_BULK_ROW()]);
    setBulkDefaultMonth("");
    setOpenBulk(true);
  };
  const addBulkRow = () => setBulkRows((rs) => [...rs, EMPTY_BULK_ROW()]);
  const removeBulkRow = (key) => setBulkRows((rs) => rs.filter((r) => r.key !== key));
  const updateBulkRow = (key, field, value) => setBulkRows((rs) => rs.map((r) => r.key === key ? { ...r, [field]: value } : r));
  const applyMonthToAll = (m) => {
    setBulkDefaultMonth(m);
    setBulkRows((rs) => rs.map((r) => r.month ? r : { ...r, month: m }));
  };
  const saveBulk = async () => {
    const expenses = bulkRows
      .filter((r) => r.month && r.expense_label.trim() && Number(r.amount) > 0)
      .map((r) => ({ month: r.month, expense_label: r.expense_label.trim(), amount: Number(r.amount), reimbursement_to: r.reimbursement_to || "BizAssist", comments: r.comments || "" }));
    if (!expenses.length) { toast.error("Fill at least one row with month, label and amount"); return; }
    try {
      const res = await api.post("/monthly-accounting/expense/bulk", { expenses });
      toast.success(`Added ${res.data.inserted} expense${res.data.inserted === 1 ? "" : "s"}`);
      setOpenBulk(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveInc = async () => {
    try {
      await api.post("/monthly-accounting/income", { ...formInc, commission_received: Number(formInc.commission_received) });
      toast.success("Saved"); setOpenInc(false); setFormInc(EMPTY_INC); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="monthly-accounting-page">
      <PageHeader title="Monthly Accounting" actions={<>
        <div className="w-36"><Select testid="ma-fy-filter" placeholder="All FYs" options={data.available_fys || []} value={fyFilter} onChange={setFyFilter} /></div>
        <div className="w-44 relative">
          <Select
            testid="ma-month-filter"
            placeholder="All months"
            options={availableMonths.map((m) => ({ value: m, label: fmtPeriod(m) }))}
            value={monthFilter}
            onChange={setMonthFilter}
          />
          {monthFilter && (
            <button
              type="button"
              data-testid="ma-month-filter-clear"
              onClick={() => setMonthFilter("")}
              className="absolute right-7 top-1/2 -translate-y-1/2 text-[var(--muted)] hover:text-[var(--ink)] text-[11px] font-medium"
              title="Clear month filter"
            >✕</button>
          )}
        </div>
        <Btn variant="ghost" testid="ma-export" onClick={() => window.open(apiUrl("/export/monthly-accounting"), "_blank")}><Download size={14} /> Export CSV</Btn>
        <Btn variant="ghost" testid="ma-add-income" onClick={() => { setFormInc(EMPTY_INC); setOpenInc(true); }}>Override Income</Btn>
        <Btn variant="ghost" testid="ma-manage-templates" onClick={openNewTemplate}>⟳ Recurring</Btn>
        <Btn variant="ghost" testid="ma-add-expense-bulk" onClick={openBulkModal}><Plus size={14} /> Add Multiple</Btn>
        <Btn variant="accent" testid="ma-add-expense" onClick={() => { setFormExp(EMPTY_EXP); setEditExpId(null); setOpenExp(true); }}><Plus size={14} /> New Expense</Btn>
      </>} />
      <div className="p-4 sm:p-10 space-y-6">
        {monthFilter && (
          <div data-testid="ma-month-filter-pill" className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
            <span className="px-2 py-1 bg-[var(--surface-2)] border border-[var(--line-2)] rounded-sm">
              <b>Filtered:</b> {fmtPeriod(monthFilter)}
            </span>
            <button onClick={() => setMonthFilter("")} className="text-[var(--accent)] hover:underline" data-testid="ma-month-filter-pill-clear">Show all months</button>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
              Gross Commission
              <span className="px-1 text-[8px] bg-[var(--accent)]/15 text-[var(--accent)] border border-[var(--accent)]/30 font-bold tracking-normal rounded-sm">= Gross</span>
            </div>
            <div className="num text-xl font-semibold mt-1.5">₹ {fmtCompact(kpiInc)}</div>
            <div className="text-[10px] text-[var(--muted)] mt-1">{monthFilter ? fmtPeriod(monthFilter) : "From Internal Margin gross revenue"}</div>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total Expenses</div>
            <div className="num text-xl font-semibold mt-1.5 text-[var(--negative)]">₹ {fmtCompact(kpiExp)}</div>
            {monthFilter && <div className="text-[10px] text-[var(--muted)] mt-1">{fmtPeriod(monthFilter)}</div>}
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4 ring-2 ring-[var(--positive)]">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold flex items-center gap-1.5">
              BizAssist's Net Revenue
              <span className="relative inline-flex group/tip" data-testid="ma-net-info-wrap">
                <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full text-[var(--muted)] hover:text-[var(--accent)] cursor-help" data-testid="ma-net-info" aria-label="About BizAssist's Net Revenue">
                  <Info size={12} strokeWidth={2} />
                </span>
                <span role="tooltip" className="pointer-events-none absolute left-1/2 top-full mt-2 -translate-x-1/2 z-20 w-64 px-3 py-2 bg-[var(--ink)] text-white text-[11px] font-normal normal-case tracking-normal leading-snug rounded-sm shadow-lg opacity-0 group-hover/tip:opacity-100 transition-opacity duration-150">
                  = Gross Commission − Total Expenses
                </span>
              </span>
            </div>
            <div className="num text-xl font-semibold mt-1.5 text-[var(--positive)]">₹ {fmtCompact(kpiNet)}</div>
            <div className="text-[10px] text-[var(--muted)] mt-1">Gross − all expenses</div>
          </div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Partner Settlements</div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div className="border-r border-[var(--line)] pr-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-sky-700">NK</div>
                <div className="num text-base font-semibold mt-0.5 text-[var(--accent)]" data-testid="kpi-nk-outstanding">₹ {fmtCompact(kpiNkOutstanding)}</div>
                <div className="text-[9.5px] text-[var(--muted)]">{kpiNkOpenMonths} mo open</div>
              </div>
              <div className="pl-2">
                <div className="text-[10px] font-bold uppercase tracking-wider text-violet-700">RR</div>
                <div className="num text-base font-semibold mt-0.5 text-[var(--accent)]" data-testid="kpi-rr-outstanding">₹ {fmtCompact(kpiRrOutstanding)}</div>
                <div className="text-[9.5px] text-[var(--muted)]">{kpiRrOpenMonths} mo open</div>
              </div>
            </div>
            <div className="text-[10px] text-[var(--muted)] mt-2">{monthFilter ? `outstanding for ${fmtPeriod(monthFilter)}` : "outstanding to settle"}</div>
          </div>
        </div>

        <PendingApprovalsTab
          pendingApprovals={pendingApprovals}
          approvalBusy={approvalBusy}
          onEdit={openApprovalEdit}
          onApprove={approveSalary}
          onReject={rejectSalary}
        />

        <MonthlySummaryTab
          filtered={filtered}
          monthFilter={monthFilter}
          setMonthFilter={setMonthFilter}
          onOpenSettlement={openSettlement}
          onOpenAlloc={openAlloc}
        />

        <ExpensesTab
          monthExpenses={monthExpenses}
          monthFilter={monthFilter}
          onEdit={editExp}
          onRemove={removeExp}
        />
      </div>

      <Modal open={openBulk} onClose={() => setOpenBulk(false)} title="Add Multiple Expenses" testid="ma-bulk-modal" size="xl">
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-2.5 bg-[var(--surface-2)] border border-[var(--line)] rounded-sm">
            <span className="text-[11px] uppercase tracking-wider text-[var(--muted)] font-semibold shrink-0">Apply month to all empty rows:</span>
            <div className="w-44"><MonthPicker value={bulkDefaultMonth} onChange={applyMonthToAll} testid="bulk-default-month" /></div>
          </div>
          <div className="overflow-auto max-h-[420px]">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-wider text-[var(--muted)] font-semibold">
                  <th className="text-left py-1.5 px-2 w-40">Month</th>
                  <th className="text-left py-1.5 px-2">Label</th>
                  <th className="text-left py-1.5 px-2 w-28">Reimb. to</th>
                  <th className="text-right py-1.5 px-2 w-28">Amount (₹)</th>
                  <th className="text-left py-1.5 px-2">Comments</th>
                  <th className="w-6"></th>
                </tr>
              </thead>
              <tbody>
                {bulkRows.map((r) => (
                  <tr key={r.key} className="border-t border-[var(--line)]" data-testid={`bulk-row-${r.key}`}>
                    <td className="p-1"><MonthPicker value={r.month} onChange={(v) => updateBulkRow(r.key, "month", v)} /></td>
                    <td className="p-1"><Input value={r.expense_label} onChange={(e) => updateBulkRow(r.key, "expense_label", e.target.value)} placeholder="e.g. Domain renewal" /></td>
                    <td className="p-1">
                      <select
                        value={r.reimbursement_to}
                        onChange={(e) => updateBulkRow(r.key, "reimbursement_to", e.target.value)}
                        className="h-9 w-full px-2 text-[12.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none"
                      >
                        <option value="BizAssist">BizAssist</option>
                        <option value="NK">NK</option>
                        <option value="RR">RR</option>
                        <option value="Partner">Partner</option>
                      </select>
                    </td>
                    <td className="p-1"><Input type="number" value={r.amount} onChange={(e) => updateBulkRow(r.key, "amount", e.target.value)} className="text-right num" /></td>
                    <td className="p-1"><Input value={r.comments} onChange={(e) => updateBulkRow(r.key, "comments", e.target.value)} placeholder="optional" /></td>
                    <td className="p-1 text-center">
                      <button onClick={() => removeBulkRow(r.key)} disabled={bulkRows.length <= 1} className="p-1 text-[var(--muted)] hover:text-[var(--negative)] disabled:opacity-30"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Btn variant="ghost" onClick={addBulkRow} testid="bulk-add-row"><Plus size={12} /> Add another row</Btn>
          <div className="text-[11px] text-[var(--muted)]">Rows missing a month, label, or amount are skipped on save.</div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpenBulk(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveBulk} testid="bulk-save">Save all</Btn>
        </div>
      </Modal>

      <Modal open={openExp} onClose={() => setOpenExp(false)} title={editExpId ? "Edit Expense" : "New Expense"} testid="ma-expense-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Month</label>
            <MonthPicker value={formExp.month} onChange={(v) => setFormExp({ ...formExp, month: v })} testid="exp-form-month" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Reimbursement to</label>
            <Select placeholder="—" options={["BizAssist", "NK", "RR"]} value={formExp.reimbursement_to} onChange={(v) => setFormExp({ ...formExp, reimbursement_to: v })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Expense Label</label>
            <Input value={formExp.expense_label} onChange={(e) => setFormExp({ ...formExp, expense_label: e.target.value })} placeholder="e.g. Domain renewal, TDS payment" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount (₹)</label>
            <Input type="number" value={formExp.amount} onChange={(e) => setFormExp({ ...formExp, amount: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Comments</label>
            <Input value={formExp.comments} onChange={(e) => setFormExp({ ...formExp, comments: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpenExp(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveExp}>{editExpId ? "Save" : "Create"}</Btn>
        </div>
      </Modal>

      <Modal open={openInc} onClose={() => setOpenInc(false)} title="Override Commission Received" testid="ma-income-modal">
        <div className="mb-3 p-2.5 bg-[var(--accent)]/5 border border-[var(--accent)]/30 text-[12px] text-[var(--ink)] rounded-sm">
          Commission Received is now auto-derived from Internal Margin gross per month. Only use this form to <b>manually override</b> a specific month (e.g. a PG-side adjustment that doesn't flow through Volumes).
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Month</label>
            <MonthPicker value={formInc.month} onChange={(v) => setFormInc({ ...formInc, month: v })} testid="inc-form-month" /></div>
          <div><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Commission Received (₹)</label>
            <Input type="number" value={formInc.commission_received} onChange={(e) => setFormInc({ ...formInc, commission_received: e.target.value })} /></div>
          <div className="col-span-2"><label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={formInc.notes} onChange={(e) => setFormInc({ ...formInc, notes: e.target.value })} /></div>
        </div>
        <div className="text-[11px] text-[var(--muted)] mt-2">Upserts the income record for this month. If one already exists, it will be updated.</div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpenInc(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={saveInc}>Save</Btn>
        </div>
      </Modal>

      <Modal open={openTmpl} onClose={() => setOpenTmpl(false)} title="Recurring Expense Templates" testid="ma-tmpl-modal" size="lg">
        <div className="space-y-4">
          <div className="p-3 bg-[var(--surface-2)] border border-[var(--line)] rounded-sm text-[12px] text-[var(--muted)]">
            Templates auto-generate an expense row every month from the start date onward. Changing or deleting a template removes future auto-rows but keeps past entries. Create one per recurring charge (e.g. office rent, subscriptions).
          </div>
          {templates.length > 0 && (
            <div className="border border-[var(--line)] rounded-sm">
              <table className="w-full text-[12.5px]">
                <thead className="bg-[var(--surface-2)] text-[10px] uppercase tracking-wider text-[var(--muted)]">
                  <tr><th className="text-left py-2 px-3">Label</th><th className="text-right px-3">Amount</th><th className="text-left px-3">Reimb to</th><th className="text-left px-3">Start</th><th className="text-left px-3">End</th><th className="w-16"></th></tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id} className="border-t border-[var(--line)]" data-testid={`tmpl-row-${t.id}`}>
                      <td className="py-2 px-3 font-medium">{t.label}</td>
                      <td className="text-right px-3 num">{Number(t.amount).toLocaleString("en-IN")}</td>
                      <td className="px-3"><Badge tone={t.reimbursement_to === "BizAssist" ? "muted" : "accent"}>{t.reimbursement_to}</Badge></td>
                      <td className="px-3 num text-[12px]">{t.start_month}</td>
                      <td className="px-3 num text-[12px]">{t.end_month || "—"}</td>
                      <td className="text-right px-2">
                        <button onClick={() => editTemplate(t)} className="p-1 hover:bg-[var(--surface-2)] rounded-sm"><Pencil size={12} /></button>
                        <button onClick={() => removeTemplate(t.id)} className="p-1 text-[var(--negative)] hover:bg-red-50 rounded-sm"><Trash2 size={12} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="border border-dashed border-[var(--line-2)] p-4 rounded-sm">
            <div className="text-[11px] uppercase tracking-[0.14em] text-[var(--muted)] font-semibold mb-3">{tmplForm.id ? "Edit template" : "New template"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Label</label>
                <Input value={tmplForm.label} onChange={(e) => setTmplForm({ ...tmplForm, label: e.target.value })} placeholder="e.g. Office rent" data-testid="tmpl-label" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount (₹)</label>
                <Input type="number" value={tmplForm.amount} onChange={(e) => setTmplForm({ ...tmplForm, amount: e.target.value })} placeholder="25000" data-testid="tmpl-amount" className="text-right num" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Reimbursement to</label>
                <select value={tmplForm.reimbursement_to} onChange={(e) => setTmplForm({ ...tmplForm, reimbursement_to: e.target.value })} data-testid="tmpl-re" className="h-9 w-full px-2 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none">
                  <option value="BizAssist">BizAssist</option>
                  <option value="NK">NK</option>
                  <option value="RR">RR</option>
                  <option value="Partner">Partner</option>
                </select>
              </div>
              <div />
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Start month</label>
                <MonthPicker value={tmplForm.start_month} onChange={(v) => setTmplForm({ ...tmplForm, start_month: v })} testid="tmpl-start" />
              </div>
              <div>
                <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">End (optional)</label>
                <MonthPicker value={tmplForm.end_month} onChange={(v) => setTmplForm({ ...tmplForm, end_month: v })} testid="tmpl-end" />
              </div>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpenTmpl(false)}>Close</Btn>
          <Btn variant="accent" onClick={saveTemplate} testid="tmpl-save">{tmplForm.id ? "Save" : "Add template"}</Btn>
        </div>
      </Modal>

      <Modal open={openPay} onClose={() => setOpenPay(false)} title={`${payForm.id ? "Edit" : "Add"} ${payForm.person} Payment · ${fmtPeriod(payForm.month)}`} testid="ma-payment-modal">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount Paid (₹)</label>
            <Input type="number" value={payForm.amount_paid} onChange={(e) => setPayForm({ ...payForm, amount_paid: e.target.value })} data-testid="pay-amount" className="text-right num" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Paid Via</label>
            <ComboSelect
              testid="pay-via"
              options={["Bank Transfer", "UPI", "Amazon Voucher", "Gift Card", "Credit Card", "Cash", "Salary Advance", "Professional Fee", "Other"]}
              value={payForm.paid_via}
              onChange={(v) => setPayForm({ ...payForm, paid_via: v })}
              placeholder="— select —"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Paid Date</label>
            <Input type="date" value={payForm.paid_date} onChange={(e) => setPayForm({ ...payForm, paid_date: e.target.value })} data-testid="pay-date" />
          </div>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
            <Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder="optional" data-testid="pay-notes" />
          </div>
        </div>
        <div className="text-[11px] text-[var(--muted)] mt-3 bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-2">
          Status auto-updates based on settled vs payable. Add multiple lines if a single month is settled across different methods (e.g. ₹50K Amazon Voucher + ₹30K Bank Transfer).
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setOpenPay(false)}>Cancel</Btn>
          <Btn variant="accent" onClick={savePayment} testid="pay-save">{payForm.id ? "Save" : "Add line"}</Btn>
        </div>
      </Modal>

      <Modal open={!!approvalEdit} onClose={() => setApprovalEdit(null)} title={approvalEdit ? `Edit & approve · ${approvalEdit.row.person_name} · ${fmtPeriod(approvalEdit.row.month)}` : ""} testid="approval-edit-modal" size="md">
        {approvalEdit && (
          <div className="space-y-3">
            <div className="text-[12px] text-[var(--muted)] bg-amber-50 border border-amber-200 rounded-sm p-3">
              Adjust the paid amount and/or notes before approving. CTC stays locked to the person's profile value (₹ {fmtINR(approvalEdit.row.amount_due, 0)} annual).
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Amount Paid (₹)</label>
              <Input
                type="number"
                value={approvalEdit.amount_paid}
                onChange={(e) => setApprovalEdit({ ...approvalEdit, amount_paid: e.target.value })}
                data-testid="approval-edit-paid"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Notes</label>
              <Input
                value={approvalEdit.notes}
                onChange={(e) => setApprovalEdit({ ...approvalEdit, notes: e.target.value })}
                placeholder="Optional"
                data-testid="approval-edit-notes"
              />
            </div>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2 mt-5">
          <Btn variant="ghost" onClick={() => setApprovalEdit(null)} disabled={!!approvalBusy}>Cancel</Btn>
          <Btn variant="ghost" testid="approval-edit-save" onClick={() => saveApprovalEdit(false)} disabled={!!approvalBusy}>
            <Pencil size={12} /> Save (keep pending)
          </Btn>
          <Btn variant="accent" testid="approval-edit-save-approve" onClick={() => saveApprovalEdit(true)} disabled={!!approvalBusy}>
            <CheckCircle2 size={12} /> Save & Approve
          </Btn>
        </div>
      </Modal>

      <Modal open={!!allocDetail} onClose={() => setAllocDetail(null)} title={allocDetail ? `${allocDetail.person} Salary Allocation · ${fmtPeriod(allocDetail.month)}` : ""} testid="ma-alloc-modal">
        {allocDetail && (
          <div className="space-y-3">
            <div className="text-[12px] text-[var(--muted)] bg-[var(--surface-2)] border border-[var(--line)] rounded-sm p-3">
              These are paid salaries to <b>{allocDetail.person}-tagged</b> people for {fmtPeriod(allocDetail.month)}, auto-deducted from {allocDetail.person}'s monthly payable. Migration entries (notes containing "Migrated") are excluded.
            </div>
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <th>Person</th>
                <th className="text-right">Amount (₹)</th>
                <th></th>
              </tr></thead>
              <tbody>
                {allocDetail.lines.map((l, i) => (
                  <tr key={`${l.name}-${i}`} data-testid={`ma-alloc-line-${i}`}>
                    <td className="font-medium">{l.name}</td>
                    <td className="text-right num text-[var(--accent)] font-semibold">{fmtINR(l.amount, 0)}</td>
                    <td className="text-[11px] text-[var(--muted)] w-[40%] truncate">{l.notes || "—"}</td>
                  </tr>
                ))}
                {!allocDetail.lines.length && <tr><td colSpan={3} className="text-center text-[var(--muted)] py-4">No allocations.</td></tr>}
                <tr className="border-t-2 border-[var(--ink)]"><td className="font-semibold">Total</td><td className="text-right num font-semibold">{fmtINR(allocDetail.lines.reduce((a, l) => a + (l.amount || 0), 0), 0)}</td><td></td></tr>
              </tbody>
            </table></div>
            <div className="text-[11px] text-[var(--muted)]">Edit individual entries from <b>People → click person → Salary history</b>.</div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4">
          <Btn variant="ghost" onClick={() => setAllocDetail(null)}>Close</Btn>
        </div>
      </Modal>

      <SettlementPanel
        settlement={settlement}
        onClose={() => setSettlement(null)}
        onOpenAlloc={openAlloc}
        onAddPayment={openPaymentNew}
        onEditPayment={openPaymentEdit}
        onDeletePayment={deletePayment}
      />

      {/* Reject-reason modal — replaces window.prompt(); matches the rest of
          the destructive-action UX (in-app shadcn-style modal). */}
      <Modal
        open={!!rejectModal}
        onClose={() => setRejectModal(null)}
        title="Reject salary entry?"
        testid="reject-salary-modal"
      >
        <div className="space-y-4">
          <p className="text-[13px] text-[var(--muted)] leading-relaxed">
            This entry will be marked rejected and <b>won't deduct</b> from the
            payable. You can leave a note for the audit trail (optional).
          </p>
          <div>
            <label className="text-[11px] uppercase text-[var(--muted)] font-semibold tracking-wider">Reason (optional)</label>
            <textarea
              data-testid="reject-reason-input"
              autoFocus
              value={rejectModal?.reason || ""}
              onChange={(e) => setRejectModal((m) => m && { ...m, reason: e.target.value })}
              placeholder="e.g. Duplicate entry, paid via voucher already…"
              rows={3}
              className="w-full px-3 py-2 mt-1 text-[13.5px] bg-[var(--surface)] border border-[var(--line-2)] rounded-sm focus:outline-none focus:border-[var(--ink)] resize-y"
            />
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <Btn variant="ghost" onClick={() => setRejectModal(null)}>Cancel</Btn>
            <Btn variant="danger" testid="reject-confirm-btn" onClick={confirmReject}>
              <X size={14} /> Reject entry
            </Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
